/**
 * Google Sheets Service for logging autofill performance data
 *
 * This service sends data to a Google Apps Script web app endpoint
 * which then logs it to the Google Sheet.
 *
 * This approach is browser-compatible and doesn't require googleapis library.
 */

/**
 * Append a row of data to the Google Sheet via Apps Script web app
 * @param {Array} values - Array of values to append as a new row
 * @returns {Promise<object>} The API response
 */
export async function appendToSheet(values) {
  // Check if tracking is enabled (fallback to "true" if env var is missing)
  const isTrackingEnabled =
    (process.env.REACT_APP_ENABLE_AUTOFILL_TRACKING || "true") === "true";

  if (!isTrackingEnabled) {
    console.log("Autofill tracking is disabled");
    return { success: false, message: "Tracking disabled" };
  }

  // Use configured URL or fallback to hardcoded URL
  const webAppUrl =
    process.env.REACT_APP_AUTOFILL_WEB_APP_URL ||
    "https://script.google.com/macros/s/AKfycbxB_xVKDeHHNSLowIerPakfyzRnjEvy1a6oIn55g8bUq-iXJPAzxNz2e-ibiHqxIjRh/exec";

  if (!webAppUrl) {
    console.error("Google Apps Script Web App URL not configured");
    console.log("Autofill Performance Data (logged to console only):", values);
    return { success: false, message: "Web App URL not configured" };
  }

  try {
    await fetch(webAppUrl, {
      method: "POST",
      mode: "no-cors", // Google Apps Script requires no-cors mode
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ values }),
    });

    // Note: With no-cors mode, we can't read the response
    // The request will succeed if it reaches the server
    console.log("Autofill performance data sent to Google Sheets");
    console.log("Data:", values);

    return { success: true, message: "Data sent successfully" };
  } catch (error) {
    console.error("Error sending data to Google Sheets:", error);
    console.log("Autofill Performance Data (logged to console only):", values);
    return { success: false, error: error.message };
  }
}

/**
 * Test the Google Sheets connection
 * @returns {Promise<boolean>} True if web app URL is configured
 */
export async function testConnection() {
  const webAppUrl = process.env.REACT_APP_AUTOFILL_WEB_APP_URL;

  if (!webAppUrl) {
    console.error("Google Apps Script Web App URL not configured");
    return false;
  }

  console.log("Google Apps Script Web App URL is configured");
  return true;
}
